<template>
  <div class="reservation-tab">
    <PageTitle title="" :btn="$t('member.tab.reservation.newReservation')" :hideBtn="!checkAction('admin.appointmentOrder.create')" @btnClick="checkAuthPhoneAccount" />
    <FiltersContainer>
      <BaseElInput
        v-model="search.code"
        clearable
        :placeholder="$t('member.tab.reservation.searchCodePlaceholder')"
        @keypress.enter.native="refresh(true)"
        @clear="refresh(true)"
      >
        <i slot="prefix" class="el-input__icon el-icon-search" @click="refresh(true)" />
      </BaseElInput>

      <TableFilter
        :tableName="tableName"
        :options="memberReservationListFilterOptions"
        @update="updateTableFilter"
      />
    </FiltersContainer>

    <!-- <div class="flex justify-end"> -->

    <!-- </div> -->

    <BaseTable
      v-if="!tableRefresh"
      v-loading="loading"
      :data="reservationsList"
      :empty-text="$t('member.tab.reservation.emptyText')"
    >
      <EmptyBlock slot="empty" />
      <BaseElTableColumn prop="code" :label="$t('member.tab.reservation.orderCode')" width="120" fixed align="center" />
      <BaseElTableColumn v-if="showColumn('orderType', tableFilter)" prop="code" :label="$t('member.tab.reservation.orderType')" fixed align="center">
        <template slot-scope="scope">
          {{ getData(scope.row, 'AppointmentOrder.peopleCount', 1) > 1 ? $t('member.tab.reservation.multiPerson') : $t('member.tab.reservation.singlePerson') }}
        </template>
      </BaseElTableColumn>
      <BaseElTableColumn
        v-if="showColumn('replyStatus', tableFilter)"
        prop="AppointmentOrder.replyStatus"
        :label="$t('reservationList.table.replyStatus.title')"
        width="120"
        align="center"
      >
        <template slot-scope="reservation">
          <Tag
            v-if="getData(reservation.row, 'AppointmentOrder.replyStatus')"
            :type="replyStatus(reservation.row).tagType"
            disable-transitions
          >
            {{ replyStatus(reservation.row).name }}
          </Tag>
          <span v-else>-</span>
        </template>
      </BaseElTableColumn>
      <BaseElTableColumn v-if="showColumn('orderStatus', tableFilter)" prop="status" :label="$t('member.tab.reservation.orderStatus')" align="center">
        <template slot-scope="scope">
          <Tag disable-transitions :type="orderStatus(getData(scope.row, 'AppointmentOrder.status'), 'tag')">
            {{ $t(orderStatus(getData(scope.row, 'AppointmentOrder.status'), 'name')) }}
          </Tag>
        </template>
      </BaseElTableColumn>
      <BaseElTableColumn
        v-if="showColumn('service', tableFilter)"
        prop="AppointmentService.name"
        :label="$t('member.tab.reservation.serviceItem')"
        align="center"
      />
      <BaseElTableColumn
        v-if="showColumn('serviceUnit', tableFilter)"
        prop="AppointmentUnit.name"
        :label="$t('member.tab.reservation.servicePerson')"
        align="center"
      />
      <BaseElTableColumn v-if="showColumn('date', tableFilter)" prop="start" :label="$t('member.tab.reservation.reservationDate')" width="120" align="center">
        <template slot-scope="scope">
          {{ dateFormat(scope.row.start) }}
        </template>
      </BaseElTableColumn>
      <BaseElTableColumn v-if="showColumn('userAddress', tableFilter) && useMemberAddress" prop="AppointmentOrder.userAddress" :label="$t('member.tab.reservation.deliveryLocation')" align="center" width="180">
        <template slot-scope="scope">
          {{ scope.row.AppointmentOrder.userAddress || '-' }}
        </template>
      </BaseElTableColumn>
      <BaseElTableColumn v-if="showColumn('origin', tableFilter)" prop="origin" :label="$t('member.tab.reservation.reservationOrigin')" width="120" align="center">
        <template slot-scope="scope">
          {{ reservationOrigins(scope.row) }}
        </template>
      </BaseElTableColumn>

      <BaseElTableColumn v-if="showColumn('orderPrice', tableFilter) && useDeposit" :label="$t('member.tab.reservation.orderTotal')" prop="status" width="105" align="center">
        <template slot-scope="scope">
          {{ orderTotalPrice(scope.row) }}
        </template>
      </BaseElTableColumn>
      <BaseElTableColumn v-if="showColumn('depositPrice', tableFilter) && useDeposit" :label="$t('member.tab.reservation.depositAmount')" prop="status" width="105" align="center">
        <template slot-scope="scope">
          {{ depositPrice(scope.row) }}
        </template>
      </BaseElTableColumn>
      <BaseElTableColumn v-if="showColumn('paymentType', tableFilter) && useDeposit" :label="$t('member.tab.reservation.paymentType')" prop="status" width="150" align="center">
        <template slot-scope="scope">
          {{ depositPaymentType(scope.row) }}
        </template>
      </BaseElTableColumn>
      <BaseElTableColumn
        v-if="showColumn('paymentStatus', tableFilter) && useDeposit"
        :label="$t('member.tab.reservation.paymentStatus')"
        prop="status"
        width="105"
        align="center"
      >
        <template slot-scope="reservation">
          <Tag
            v-if="getData(reservation.row, 'AppointmentOrder.AppointmentOrderDeposit') && !isBatchOrder(reservation.row)"
            :type="paymentStatusTagType(reservation.row, 'deposit')"
            disable-transitions
          >
            {{ paymentStatus(reservation.row, 'deposit') }}
          </Tag>
          <span v-else>-</span>
        </template>
      </BaseElTableColumn>

      <BaseElTableColumn v-if="showColumn('checkoutTotalPrice', tableFilter)" :label="$t('member.tab.reservation.actualAmount')" align="center">
        <template slot-scope="scope">
          {{ getData(scope.row, 'AppointmentOrder.AppointmentCheckoutOrder.totalPrice', '-') }}
        </template>
      </BaseElTableColumn>
      <BaseElTableColumn v-if="showColumn('checkoutPaymentType', tableFilter)" :label="$t('member.tab.reservation.checkoutPaymentType')" width="120px" align="center">
        <template slot-scope="scope">
          {{ displayCheckoutPaymentType(scope.row) }}
        </template>
      </BaseElTableColumn>
      <BaseElTableColumn
        v-if="showColumn('checkoutPaymentStatus', tableFilter)"
        :label="$t('member.tab.reservation.checkoutPaymentStatus')"
        prop="status"
        width="105"
        align="center"
      >
        <template slot-scope="reservation">
          <Tag
            v-if="getData(reservation.row, 'AppointmentOrder.AppointmentCheckoutOrder.AppointmentCheckoutPayment') && !isBatchOrder(reservation.row)"
            :type="paymentStatusTagType(reservation.row, 'checkout')"
            disable-transitions
          >
            {{ paymentStatus(reservation.row, 'checkout') }}
          </Tag>
          <span v-else>-</span>
        </template>
      </BaseElTableColumn>

      <BaseElTableColumn
        v-if="showColumn('batchOrderStatus', tableFilter)"
        :label="$t('reservationList.table.batchOrderStatus.title')"
        prop="status"
        width="105"
        align="center"
      >
        <template slot-scope="reservation">
          <Tag v-if="isBatchOrder(reservation.row)" disable-transitions :type="getData(batchOrderStatus(reservation.row), 'tagType')">
            {{ getData(batchOrderStatus(reservation.row), 'name') }}
          </Tag>
          <span v-else>-</span>
        </template>
      </BaseElTableColumn>

      <BaseElTableColumn fixed="right" :label="$t('member.tab.reservation.action')" width="120" align="center">
        <template slot-scope="reservation">
          <BaseElButton
            slot="reference"
            type="text"
            icon="el-icon-setting"
            @click="onDetail(reservation.row)"
          />
        </template>
      </BaseElTableColumn>
    </BaseTable>

    <Pagination
      :curPage.sync="tableOptions.page"
      :pageLimit="tableOptions.pageLimit"
      :total="reservationsCount"
      @pageChange="refresh(false)"
    />

    <ReservationOrderDetail
      v-if="showInfo"
      :orderId="selectRow.id"
      :selecReservation="selectRow"
      :enableCancelReasonCollection="enableCancelReasonCollection"
      @close="showInfo=false"
      @cancel="onCancel"
      @refresh="refresh"
    />

    <DeleteDialog
      v-if="deleteDialog"
      :title="$t('member.tab.reservation.cancelReservation')"
      :content="deleteContent"
      width="40%"
      :btnString="$t('member.tab.reservation.cancelReservation')"
      :cancelBtnString="$t('member.tab.reservation.back')"
      @close="deleteDialog = false"
      @delete="cancelReservation(), deleteDialog=false"
    />
  </div>
</template>

<script>
import TableFilter from '@/components/Button/TableFilter.vue'
import ReservationOrderDetail from '@/components/Reservation/ReservationOrderDetail/ReservationOrderDetail.vue'
import EmptyBlock from '@/components/EmptyBlock.vue'
import DeleteDialog from '@/components/Dialog/DeleteDialog'
import MixinFunc from '@/components/MixinFunc.vue'

import { GetReservation, GetReservationCount, UpdateReservationOrderStatus } from '@/api/reservation'
import { paymentOptionsConfig } from '@/config/payment'
import { getTableFilterConfig, showColumn } from '@/utils/tableFilter'
import { pageStartIndex } from '@/utils/table'
import { reservationOrderOrigins } from '@/utils/reservation'
import { statusTranslate } from '@/utils/helper'
import { getData } from '@/utils/object'

import { depositPaymentTypes, depositStatusConfig } from '@/config/deposit'
import { memberReservationListFilterOptions } from '@/config/table'
import { batchOrderStatusConfig, orderStatusConfig, replyStatusConfig } from '@/config/reservation'
import { checkUserFeature } from '@/store/modules/permission'
import { mapGetters } from 'vuex'
import { get } from 'lodash'
import { usePayment } from '@/use/usePayment'
import { useReservation } from '@/use/useReservation'
import { onMounted, computed } from 'vue'
import { i18n } from '@/plugins/i18n/i18n'

export default {
  name: 'ReservationTab',
  components: {
    DeleteDialog,
    EmptyBlock,
    ReservationOrderDetail,
    TableFilter,
  },
  mixins: [MixinFunc],
  props: {
    member: {
      type: Object,
      required: true,
    },
    externalWalletName: {
      type: String,
      default: '第三方儲值金',
    },
  },
  setup (props) {
    const { getDisplayPaymentType } = usePayment()
    const { configData, getConfig } = useReservation()
    const enableCancelReasonCollection = computed(() => get(configData.reservation, 'enableCancelReasonCollection', false))

    // !! 暫時作法，以後需棄用或重構
    // ?? 是否顯示付款狀態 (定金、結帳)
    const isBatchOrder = computed(() => {
      return (row) => {
        const orderData = get(row, 'AppointmentOrder')
        if (get(orderData, 'hasBatchOrder')) {
          return true
        }

        return false
      }
    })

    const batchOrderStatus = computed(() => {
      return (row) => {
        const status = get(row, 'AppointmentOrder.AppointmentBatchOrder.StandardPaymentOrder.status')
        return {
          tagType: get(batchOrderStatusConfig, `${status}.tag`),
          name: i18n.t(get(batchOrderStatusConfig, `${status}.name`)),
        }
      }
    })

    const replyStatus = computed(() => {
      return (row) => {
        const status = get(row, 'AppointmentOrder.replyStatus')
        return {
          tagType: get(replyStatusConfig, `${status}.tag`),
          name: i18n.t(get(replyStatusConfig, `${status}.name`)),
        }
      }
    })

    const displayCheckoutPaymentType = (row) => {
      const checkoutOrder = get(row, 'AppointmentOrder.AppointmentCheckoutOrder')
      const checkoutPaymentType = get(checkoutOrder, 'AppointmentCheckoutPayment.paidType')
      const checkoutPaymentTypeComment = get(checkoutOrder, 'AppointmentCheckoutPayment.paidTypeComment')
      const displayCheckoutPaidType = getDisplayPaymentType({
        paidType: checkoutPaymentType,
        paidTypeComment: checkoutPaymentTypeComment,
      })
      if (checkoutPaymentType === 'externalWallet') return props.externalWalletName

      return displayCheckoutPaidType
    }
    onMounted(() => {
      // externalWalletConfig 已從父組件通過 prop 傳遞，無需額外呼叫
    })
    return {
      paymentOptionsConfig,
      getDisplayPaymentType,
      displayCheckoutPaymentType,
      isBatchOrder,
      batchOrderStatus,
      replyStatus,
      getConfig,
      enableCancelReasonCollection,
    }
  },

  data: () => ({
    tableRefresh: false,
    // memberReservationListFilterOptions,
    tableName: 'memberReservationTab',
    tableFilter: [],
    deleteContent: '',
    loading: false,
    showInfo: false,
    selectRow: {},
    deleteDialog: false,
    dialogType: '',
    reservationsCount: 0,
    reservationsList: [],
    tableOptions: {
      page: 1,
      pageLimit: 10,
    },
    search: {
      code: '',
      customFlowId: '',
    },
  }),
  computed: {
    ...mapGetters([
      'shop',
      'userPlanLimit',
      'userFeatures',
      'userPlanFeature',
    ]),
    shop () {
      return this.$store.getters.shop
    },
    pageStartIndex () {
      return pageStartIndex(this.tableOptions.page, this.tableOptions.pageLimit)
    },
    useDeposit () {
      return checkUserFeature(this.userPlanFeature, this.userFeatures, 'adminView.appointmentDeposit.enable')
    },
    memberReservationListFilterOptions () {
      const blockList = []
      if (!this.useDeposit) {
        blockList.push(...['orderPrice', 'depositPrice', 'paymentType', 'paymentStatus'])
      }
      if (!this.useMemberAddress) {
        blockList.push('userAddress')
      }
      return memberReservationListFilterOptions.filter(i => !blockList.includes(i.value))
      // return memberReservationListFilterOptions
    },
    useMemberAddress () {
      return this.checkAction('adminView.member.findUserInfoMoreAddress')
    },
  },
  async mounted () {
    this.tableFilter = getTableFilterConfig(this.tableName, memberReservationListFilterOptions)
    await this.refresh()

    // 取得預約設定（取消原因收集開關）；擺最後避免設定取得失敗時擋住列表載入
    await this.getConfig('reservation')
  },
  methods: {
    showColumn,
    getData,
    onDetail (row) {
      this.selectRow = row
      this.showInfo = true
    },
    checkAction (action) {
      return checkUserFeature(this.userPlanFeature, this.userFeatures, action)
    },
    orderStatus (status, attr) {
      return get(orderStatusConfig[status], attr)
    },
    onCancel (data, cancelReason) {
      if (!this.checkAction('admin.appointmentReservation.cancel')) return
      // 取消原因由抽屜內部的彈窗收集後一併帶回，直接送出
      if (cancelReason) {
        this.cancelReservation(cancelReason)
        return
      }
      const orders = get(data, 'AppointmentOrder.AppointmentReservations')
      const orderCode = get(data, 'AppointmentOrder.code')
      // const numberCodes = map(orders, 'numberCode')
      // const totalOrde = max(numberCodes)
      // this.deleteDialog = true
      if (!orders) {
        this.deleteDialog = true
        this.deleteContent = i18n.t('member.tab.reservation.confirmCancel')
        return
      }
      if (!orders.length || orders.length === 1) {
        this.deleteDialog = true
        this.deleteContent = i18n.t('member.tab.reservation.confirmCancel')
        return
      }

      const ordersCount = orders.length

      this.deleteContent = i18n.t('member.tab.reservation.confirmCancelWithCount', { code: orderCode, count: ordersCount })
      this.deleteDialog = true
    },
    async refresh (search = false) {
      if (search) this.tableOptions.page = 1
      this.loading = true
      this.getReservation()
      this.getReservationCount()
      this.loading = false
    },

    reservationTagType (val) {
      let type = 'info'
      if (val === 'complete') type = 'action'
      if (val === 'wait') type = 'info'
      if (val === 'cancel') type = 'danger'
      return type
    },
    statusTrans (status) {
      return statusTranslate(status)
    },
    reservationOrigins (row) {
      const notSpecify = row.fromNotSpecify
      const origin = getData(row, 'AppointmentOrder.origin', '')
      const originText = getData(row, 'AppointmentOrder.originText', '')
      return reservationOrderOrigins({ notSpecify, origin, originText })
    },

    //= > 取得預約總數
    async getReservationCount () {
      try {
        this.reservationsCount = await GetReservationCount({
          shopId: this.shop,
          code: (this.search.code === '') ? undefined : this.search.code,
          MemberId: this.member.id,
          // customFlowNodeId: this.search.customFlowId || undefined,
        })
      } catch (error) {
        this.$message.error({
          message: error || error.message,
        })
      }
    },

    //= > 取得預約
    async getReservation () {
      try {
        const res = await GetReservation({
          shopId: this.shop,
          start: this.pageStartIndex,
          limit: this.tableOptions.pageLimit,
          MemberId: this.member.id,
          code: (this.search.code === '') ? undefined : this.search.code,
          includeAppointmentOrderCheckIn: this.checkAction('admin.checkInPageSetting.page') || this.checkAction('planModule.selfCheckIn.enable'),
          includeAppointmentReservationTicket: this.checkAction('admin.classTicket.page'),
          // includeCustomFlowRecord: this.useCustomFlowFeature.record,
          includeAppointmentOrderDeposit: showColumn('depositPaymentStatus', this.tableFilter) || showColumn('depositPrice', this.tableFilter),
          includeAppointmentCheckoutOrder: showColumn('checkoutPaymentStatus', this.tableFilter),
          // customFlowNodeId: this.search.customFlowId || undefined,
        })

        this.reservationsList = res.filter(
          (item) => item.MemberId === this.member.id,
        )
      } catch (error) {
        this.$message.error({
          message: error || error.message,
        })
      }
    },

    //= > 取消預約
    // async cancelReservation () {
    //   try {
    //     await UpdateReservationStatus({
    //       shopId: this.shop,
    //       id: this.selectRow.id,
    //       status: 'cancel',
    //     })
    //     this.$message.success('取消預約成功')
    //     this.deleteDialog = false
    //     this.showInfo = false
    //     this.refresh()
    //   } catch (error) {
    //     console.log(error)
    //     this.$message.error({
    //       message: error || error.message,
    //     })
    //   }
    // },

    //= > 刪除(取消)預約
    async cancelReservation ({ cancelReasonCode, cancelReasonNote } = {}) {
      const [, err] = await UpdateReservationOrderStatus({
        shopId: this.shop,
        id: get(this.selectRow, 'AppointmentOrder.id'),
        status: 'cancel',
        cancelReasonCode,
        cancelReasonNote,
      })

      if (err) return this.$message.error(err)
      this.$message.success(i18n.t('member.tab.reservation.cancelSuccess'))
      this.deleteDialog = false
      this.showInfo = false
      this.refresh()
    },

    //= > 檢查是否有綁定手機
    checkAuthPhoneAccount () {
      if (this.member.isBlock) return this.$message.error(i18n.t('member.tab.reservation.blockedMemberError'))
      if (!this.member.UserInfo.phone || this.member.UserInfo.phone === '') return this.$message.error(i18n.t('member.tab.reservation.noPhoneError'))
      else this.$router.push({ name: 'NewReservationCreate', query: { member: this.member.id } })
    },

    async updateTableFilter () {
      // this.loading = true
      this.tableRefresh = true
      this.tableFilter = getTableFilterConfig(this.tableName)
      await this.refresh()
      setTimeout(() => {
        this.tableRefresh = false
      }, 0)
      // this.loading = false
    },

    orderTotalPrice (row) {
      const orderPrice = get(row, 'AppointmentOrder.totalPrice')
      if (!orderPrice) return '-'
      return `$ ${orderPrice}`
    },

    depositPaymentType (row) {
      const depositData = get(row, 'AppointmentOrder.AppointmentOrderDeposit')
      if (!depositData) return '-'
      const type = depositData.paidType
      if (type === 'offline') {
        const comment = depositData.paidTypeComment
        return i18n.t(get(depositPaymentTypes[comment], 'name'))
      }
      if (type === 'externalWallet') {
        return this.externalWalletName
      }
      return i18n.t(get(depositPaymentTypes[type], 'name'))
    },

    depositPrice (row) {
      const depositData = get(row, 'AppointmentOrder.AppointmentOrderDeposit')
      if (!depositData) return '-'
      if (depositData.depositPrice === 0) return '-'
      return `$ ${depositData.depositPrice}`
    },
    paymentStatus (row, type) {
      if (type === 'deposit') {
        const depositData = get(row, 'AppointmentOrder.AppointmentOrderDeposit')
        if (!depositData) return '-'
        const status = depositData.status
        const paidType = depositData.paidType
        // 當付款方式為免付定金且狀態為已付款時,顯示「免付定金」
        const displayStatus = (paidType === 'free' && status === 'paid') ? 'free' : status
        return i18n.t(get(depositStatusConfig[displayStatus], 'name'))
      } else if (type === 'checkout') {
        const depositData = get(row, 'AppointmentOrder.AppointmentCheckoutOrder.AppointmentCheckoutPayment')
        if (!depositData) return '-'
        const status = depositData.status
        return i18n.t(get(depositStatusConfig[status], 'name'))
      }
    },
    paymentStatusTagType (row, type) {
      if (type === 'deposit') {
        const depositData = get(row, 'AppointmentOrder.AppointmentOrderDeposit')
        if (!depositData) return ''
        const status = depositData.status
        const paidType = depositData.paidType
        // 當付款方式為免付定金且狀態為已付款時,使用 free 狀態的 tag
        const displayStatus = (paidType === 'free' && status === 'paid') ? 'free' : status
        return get(depositStatusConfig[displayStatus], 'tag')
      } else if (type === 'checkout') {
        const checkoutData = get(row, 'AppointmentOrder.AppointmentCheckoutOrder.AppointmentCheckoutPayment')
        if (!checkoutData) return ''
        const status = checkoutData.status
        return get(depositStatusConfig[status], 'tag')
      }
    },
  },
}
</script>

<style scoped lang="scss"></style>
